import { parseISO, format as formatDateFns, isValid as isValidDate, addYears, addDays, differenceInDays, getDaysInYear } from 'date-fns';
import { calculateEndOfLife } from './lifeExpectancyCalculator';
import { calculateWorklifeExpectancy } from './worklifeExpectancyCalculator';


export const calculateInitialStatisticalData = async (formData) => {
    const { claimantDOB, claimantGender, claimantCountry, claimantState, claimantEducation, claimantEmploymentStatus, claimantSector } = formData;
    let newCalculations = {};

    if (claimantDOB && claimantCountry && (claimantCountry !== 'Canada' || claimantState)) {
        const lifeExpectancyResult = await calculateEndOfLife(claimantDOB, claimantGender, claimantCountry, claimantState);
        if (lifeExpectancyResult) {
            newCalculations.calculatedEndOfLife = lifeExpectancyResult.endDate;
            newCalculations.calculatedLifeExpectancyYears = lifeExpectancyResult.expectancyYears;
            newCalculations.lifeExpectancyYears = lifeExpectancyResult.expectancyYears;
            newCalculations.expectedEndOfLife = lifeExpectancyResult.endDate;
        }
    }

    if (claimantDOB) {
        const educationParam = (claimantCountry === 'US' || claimantCountry === 'Canada') ? claimantEducation : undefined;
        const worklifeResult = await calculateWorklifeExpectancy(
            claimantDOB,
            claimantGender,
            educationParam,
            claimantCountry,
            claimantEmploymentStatus,
            claimantSector
        );
        if (worklifeResult) {
            newCalculations.calculatedWorklifeExpectancy = worklifeResult.worklifeYears;
            newCalculations.calculatedRetirementDate = worklifeResult.retirementDate;
            newCalculations.worklifeExpectancyYears = worklifeResult.worklifeYears;
            newCalculations.retirementDate = worklifeResult.retirementDate;
        }
    }

    return newCalculations;
};

export const calculateCustomEndOfLife = (lifeExpectancyYears, valuationDateStr) => {
    const valuationDateForCalc = safeParseISO(valuationDateStr);
    if (!isValidDate(valuationDateForCalc) || lifeExpectancyYears === null || lifeExpectancyYears === '' || isNaN(parseFloat(lifeExpectancyYears))) {
        return null;
    }

    const expectancy = parseFloat(parseFloat(lifeExpectancyYears).toFixed(2));
    if (expectancy < 0) return null;

    const fullYears = Math.floor(expectancy);
    const fractionalYear = expectancy - fullYears;
    const daysInTargetYear = getDaysInYear(addYears(valuationDateForCalc, fullYears));
    const daysFromFraction = Math.round(fractionalYear * daysInTargetYear);

    let calculatedEOL = addYears(valuationDateForCalc, fullYears);
    calculatedEOL = addDays(calculatedEOL, daysFromFraction);
    return formatDateFns(calculatedEOL, 'yyyy-MM-dd');
};

export const calculateCustomLifeExpectancyYears = (expectedEndOfLife, valuationDateStr) => {
    const valuationDateForCalc = safeParseISO(valuationDateStr);
    const eolDate = safeParseISO(expectedEndOfLife);

    if (!isValidDate(valuationDateForCalc) || !isValidDate(eolDate)) {
        return null;
    }

    if (eolDate > valuationDateForCalc) {
        const totalDaysDifference = differenceInDays(eolDate, valuationDateForCalc);
        return (totalDaysDifference / 365.25).toFixed(2);
    }
    
    return '0.00';
};

export const calculateCustomRetirementDate = (worklifeExpectancyYears, valuationDateStr) => {
    const valuationDateForCalc = safeParseISO(valuationDateStr);
    if (!isValidDate(valuationDateForCalc) || worklifeExpectancyYears === null || worklifeExpectancyYears === '' || isNaN(parseFloat(worklifeExpectancyYears))) {
        return null;
    }
    
    const expectancy = parseFloat(parseFloat(worklifeExpectancyYears).toFixed(2));
    if (expectancy < 0) return null;

    const fullYears = Math.floor(expectancy);
    const fractionalYear = expectancy - fullYears;
    const daysInTargetYear = getDaysInYear(addYears(valuationDateForCalc, fullYears));
    const daysFromFraction = Math.round(fractionalYear * daysInTargetYear);

    let calculatedRetirement = addYears(valuationDateForCalc, fullYears);
    calculatedRetirement = addDays(calculatedRetirement, daysFromFraction);
    return formatDateFns(calculatedRetirement, 'yyyy-MM-dd');
};

export const calculateCustomWorklifeExpectancyYears = (retirementDate) => {
    const currentDate = new Date();
    const retirementDt = safeParseISO(retirementDate);

    if (!isValidDate(retirementDt)) {
        return null;
    }

    if (retirementDt > currentDate) {
        const totalDaysDifference = differenceInDays(retirementDt, currentDate);
        return (totalDaysDifference / 365.25).toFixed(2);
    }
    
    return '0.00';
};

export const calculateRetirementAndLifespanAge = (claimantDOB, retirementDate, expectedEndOfLife) => {
    const dob = safeParseISO(claimantDOB);
    const retDate = safeParseISO(retirementDate);
    const eolDate = safeParseISO(expectedEndOfLife);
    let ages = {};

    if (isValidDate(dob) && isValidDate(retDate) && retDate >= dob) {
        const daysDifference = differenceInDays(retDate, dob);
        ages.retirementAge = (daysDifference / 365.25).toFixed(2);
    }

    if (isValidDate(dob) && isValidDate(eolDate) && eolDate >= dob) {
        const daysDifference = differenceInDays(eolDate, dob);
        ages.lifespanAge = (daysDifference / 365.25).toFixed(2);
    }

    return ages;
};

export const calculateNetDiscountRate = (discountRate, growthRate) => {
    const dr = parseFloat(discountRate);
    const gr = parseFloat(growthRate);

    if (!isNaN(dr) && !isNaN(gr)) {
        const netRate = ((1 + dr) / (1 + gr)) - 1;
        return parseFloat(netRate.toFixed(4));
    }
    return null;
};

export const triggerAndSetCalculations = (formData) => {
    const {
      incidentDate,
      valuationDate,
      incomePeriods,
      expectedEndOfLife,
      retirementDate,
    } = formData;

    if (incidentDate && valuationDate && incomePeriods && incomePeriods.length > 0) {
      const calculationInputs = {
        incidentDetails: {
          incidentDate: incidentDate,
          estimatedSettlementDate: valuationDate,
          discountRate: formData.discountRate,
        },
        lossOfIncome: {
          sourcesOfIncome: incomePeriods,
          growthRate: formData.growthRate,
        },
        lifeExpectancyDateString: expectedEndOfLife,
        retirementDateString: retirementDate,
      };
      try {
        return calculateLossOfIncome(calculationInputs, formData);
      } catch (error) {
        console.error("Error calculating loss of income:", error);
        return {
          summary: { totalPastLoss: 0, totalFutureLoss: 0, preJudgementInterest: 0, presentValueDiscount: 0, grandTotalLoss: 0, error: "Calculation error" },
          detailedPastLosses: [],
          detailedFutureLosses: [],
        };
      }
    }
    return {
      summary: { totalPastLoss: 0, totalFutureLoss: 0, preJudgementInterest: 0, presentValueDiscount: 0, grandTotalLoss: 0 },
      detailedPastLosses: [],
      detailedFutureLosses: [],
    };
};

export const PayInterval = Object.freeze({
  PER_YEAR: 'PerYear',
  ONCE_PER_MONTH: 'OncePerMonth',
  TWICE_PER_MONTH: 'TwicePerMonth',
  EVERY_TWO_WEEKS: 'EveryTwoWeeks',
  EVERY_WEEK: 'EveryWeek',
  EVERY_DAY: 'EveryDay',
});

// Mapping of PayInterval to the number of pay periods in a year.
// Used for converting annual growth rates to per-period rates.
export const PAY_PERIODS_PER_YEAR_MAP = Object.freeze({
  [PayInterval.PER_YEAR]: 1,
  [PayInterval.ONCE_PER_MONTH]: 12,
  [PayInterval.TWICE_PER_MONTH]: 24, // e.g., semi-monthly
  [PayInterval.EVERY_TWO_WEEKS]: 26, // Bi-weekly
  [PayInterval.EVERY_WEEK]: 52,
  [PayInterval.EVERY_DAY]: 365, // As per typical financial calculations, not 365.25
});

export const DurationUnit = Object.freeze({
  DAYS: 'Days',
  WEEKS: 'Weeks',
  MONTHS: 'Months',
  YEARS: 'Years',
});

export const TimePeriodEndType = Object.freeze({
  END_DATE: 'EndDate',
  DURATION: 'Duration',
  LIFETIME: 'Lifetime', // Added for completeness, though not fully handled by determineSourceEndDate yet
});

// --- Date Utility Functions ---

export function getStartOfDay(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}


export function addMonths(date, months) {
  const d = new Date(date);
  d.setMonth(d.getMonth() + months);
  return d;
}

export function getStartOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

// Calculates number of days from start of startDate to end of endDate, inclusive.
// e.g., Jan 1 to Jan 1 is 1 day. Jan 1 to Jan 2 is 2 days.
export function getDurationInDays(startDate, endDate) {
  const start = getStartOfDay(startDate);
  const end = getStartOfDay(endDate); // Use start of day for both for simpler diff

  if (end < start) return 0;

  const diffTime = end.getTime() - start.getTime();
  const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
  return diffDays + 1;
}

export function getDaysInSpecificMonth(year, month) { // month is 0-indexed
  return new Date(year, month + 1, 0).getDate();
}

export function getDaysInMonthOfDate(date) {
    return getDaysInSpecificMonth(date.getFullYear(), date.getMonth());
}

// Generates monthly (or partial monthly) periods between an overall start and end date.
// Dates are inclusive.
export function getMonthlyPeriods(overallStartDate, overallEndDate) {
  const periods = [];
  let currentPeriodStart = safeParseISO(overallStartDate);
  const finalEndDate = safeParseISO(overallEndDate);

  if (finalEndDate < currentPeriodStart) {
    return periods;
  }

  while (currentPeriodStart <= finalEndDate) {
    let currentPeriodEnd;
    const startOfNextMonth = getStartOfMonth(addMonths(currentPeriodStart, 1));

    if (startOfNextMonth <= finalEndDate && addDays(startOfNextMonth, -1) < finalEndDate) {
      currentPeriodEnd = addDays(startOfNextMonth, -1);
    } else {
      currentPeriodEnd = finalEndDate;
    }
    
    if (currentPeriodEnd > finalEndDate) {
        currentPeriodEnd = finalEndDate;
    }

    periods.push({ start: new Date(currentPeriodStart), end: new Date(currentPeriodEnd) });

    currentPeriodStart = addDays(currentPeriodEnd, 1);
    currentPeriodStart = getStartOfDay(currentPeriodStart); 
  }
  return periods;
}

export function determineSourceEndDate(incomeSource) {
  if (!incomeSource || !incomeSource.startDate) return undefined;

  const sourceStartDate = safeParseISO(incomeSource.startDate);

  if (incomeSource.endType === 'EndDate' && incomeSource.endDate) {
    return safeParseISO(incomeSource.endDate);
  } else if (incomeSource.endType === 'Duration' && incomeSource.duration && incomeSource.durationUnit) {
    let endDate = new Date(sourceStartDate);
    const duration = parseInt(incomeSource.duration, 10);

    if (isNaN(duration) || duration <= 0) {
        // console.warn("Invalid duration:", incomeSource.duration);
        return undefined;
    }

    switch (incomeSource.durationUnit) {
      case DurationUnit.DAYS:
        endDate = addDays(endDate, duration - 1);
        break;
      case DurationUnit.WEEKS:
        endDate = addDays(endDate, duration * 7 - 1);
        break;
      case DurationUnit.MONTHS:
        endDate = addMonths(endDate, duration);
        endDate = addDays(endDate, -1);
        break;
      case DurationUnit.YEARS:
        endDate = addMonths(endDate, duration * 12);
        endDate = addDays(endDate, -1);
        break;
      default:
        // console.warn("Unknown duration unit:", incomeSource.durationUnit);
        return undefined;
    }
    return getStartOfDay(endDate);
  }
  // console.warn("Could not determine end date for income source:", incomeSource.description);
  return undefined;
}

// --- Growth Calculation Helper Functions ---

/**
 * Gets the number of pay periods per year for a given pay interval.
 * @param {string} payInterval - A value from PayInterval enum.
 * @returns {number} The number of pay periods per year.
 */
function getPayPeriodsPerYear(payInterval) {
  const periods = PAY_PERIODS_PER_YEAR_MAP[payInterval];
  if (periods === undefined) {
    // console.warn("getPayPeriodsPerYear: Unknown pay interval, defaulting to 0:", payInterval);
    return 0;
  }
  return periods;
}

/**
 * Calculates the per-period growth rate from an annual growth rate.
 * r_p = (1 + r_annual)^(1/n) - 1
 * @param {number} annualGrowthRate - The annual growth rate (e.g., 0.02 for 2%).
 * @param {number} payPeriodsPerYear - The number of pay periods per year (n).
 * @returns {number} The per-period growth rate.
 */
function calculatePerPeriodGrowthRate(annualGrowthRate, payPeriodsPerYear) {
  if (payPeriodsPerYear <= 0) {
    // This case should ideally not be reached if payPeriodsPerYear is validated.
    // console.warn("calculatePerPeriodGrowthRate: payPeriodsPerYear is zero or negative.", payPeriodsPerYear);
    return 0;
  }
  if (annualGrowthRate === 0) {
    return 0;
  }
  // Ensure base is not negative if annualGrowthRate < -1, though typically growth rates aren't that low.
  const base = 1 + annualGrowthRate;
  if (base < 0 && (1 / payPeriodsPerYear) % 1 !== 0) { // Fractional exponent of a negative number
      // console.warn("calculatePerPeriodGrowthRate: Cannot calculate fractional power of a negative base.", base, payPeriodsPerYear);
      return 0; // Or handle as an error, this implies an extreme negative annual rate.
  }
  return Math.pow(base, 1 / payPeriodsPerYear) - 1;
}

/**
 * Calculates the number of growth applications (completed pay periods)
 * between a start date and a reference date.
 * @param {Date} periodStartDate - The date from which growth counting begins (inclusive for the start of the first period).
 * @param {Date} referenceDate - The date up to which completed growth periods are counted (exclusive for the start of this period).
 * @param {number} payPeriodsPerYear - The number of pay periods per year.
 * @returns {number} The number of times growth should have been applied.
 */
function calculateNumberOfGrowthApplications(periodStartDate, referenceDate, payPeriodsPerYear) {
  const startD = getStartOfDay(new Date(periodStartDate));
  const refD = getStartOfDay(new Date(referenceDate));

  if (refD <= startD) return 0;
  if (payPeriodsPerYear <= 0) return 0;

  // Calculate duration in days between the two dates.
  // getTime() returns milliseconds since epoch.
  const daysElapsed = (refD.getTime() - startD.getTime()) / (1000 * 60 * 60 * 24);

  if (daysElapsed <= 0) return 0;

  // Use 365.25 for a more accurate average number of days in a year,
  // ensuring consistency with how financial years are often averaged.
  const averagePeriodLengthInDays = 365.25 / payPeriodsPerYear;
  if (averagePeriodLengthInDays <= 0) return 0;

  // Number of full average periods that have passed.
  // This determines how many times the per-period growth rate has compounded.
  const numApplications = Math.floor(daysElapsed / averagePeriodLengthInDays);
  return numApplications;
}

// --- Core Calculation Functions ---

export function calculateIncomeForSegment(
  segmentStartDate,
  segmentEndDate,
  incomeAmount,
  payInterval
) {
  const daysInSegment = getDurationInDays(segmentStartDate, segmentEndDate);
  if (daysInSegment <= 0) return 0;

  let calculatedIncome = 0;

  if (payInterval === PayInterval.EVERY_DAY) {
    calculatedIncome = incomeAmount * daysInSegment;
  } else {
    let monthlyEquivalentIncome;
    const daysInMonthOfSegmentStart = getDaysInMonthOfDate(segmentStartDate);

    if (daysInMonthOfSegmentStart <= 0) { // Should not happen with valid dates
        // console.error("Error: Days in month of segment start is zero or negative.", segmentStartDate);
        return 0;
    }

    switch (payInterval) {
      case PayInterval.PER_YEAR:
        monthlyEquivalentIncome = incomeAmount / 12;
        break;
      case PayInterval.ONCE_PER_MONTH:
        monthlyEquivalentIncome = incomeAmount;
        break;
      case PayInterval.TWICE_PER_MONTH:
        monthlyEquivalentIncome = incomeAmount * 2;
        break;
      case PayInterval.EVERY_TWO_WEEKS:
        monthlyEquivalentIncome = incomeAmount * (daysInMonthOfSegmentStart / 14);
        break;
      case PayInterval.EVERY_WEEK:
        monthlyEquivalentIncome = incomeAmount * (daysInMonthOfSegmentStart / 7);
        break;
      default:
        // console.error("Unknown or unhandled PayInterval for monthly normalization:", payInterval);
        return 0;
    }
    const prorationFactor = daysInSegment / daysInMonthOfSegmentStart;
    calculatedIncome = monthlyEquivalentIncome * prorationFactor;
  }
  return calculatedIncome;
}

export function calculateGrossIncomeForDateRange(
  incomeSourceAmount,
  incomeSourcePayInterval,
  rangeStartDate,
  rangeEndDate
) {
  if (typeof incomeSourceAmount !== 'number' || !incomeSourcePayInterval || !rangeStartDate || !rangeEndDate) {
    // console.error("Invalid parameters for calculateGrossIncomeForDateRange:",
      // {incomeSourceAmount, incomeSourcePayInterval, rangeStartDate, rangeEndDate});
    return 0;
  }

  const overallStart = safeParseISO(rangeStartDate);
  const overallEnd = safeParseISO(rangeEndDate);

  if (overallEnd < overallStart) {
    return 0;
  }

  const monthlyPeriods = getMonthlyPeriods(overallStart, overallEnd);
  let totalGrossIncome = 0;

  for (const period of monthlyPeriods) {
    const segmentIncome = calculateIncomeForSegment(
      period.start,
      period.end,
      incomeSourceAmount,
      incomeSourcePayInterval
    );
    totalGrossIncome += segmentIncome;
  }
  return totalGrossIncome;
}

// --- Main Orchestration Function ---

export function calculateLossOfIncome(inputs, reportData) {
  if (!inputs || !inputs.incidentDetails || !inputs.lossOfIncome || !inputs.lossOfIncome.sourcesOfIncome) {
    // console.error("Missing critical inputs structure for calculateLossOfIncome.");
    return { summary: { totalPastLoss: 0, totalFutureLoss: 0, preJudgementInterest: 0, presentValueDiscount: 0, grandTotalLoss: 0 }, detailedPastLosses: [], detailedFutureLosses: [] };
  }

  const { incidentDetails, lossOfIncome, lifeExpectancyDateString, retirementDateString } = inputs;
  const { growthRate: growthRateStringRaw } = lossOfIncome; // Added for growth rate
  const { incidentDate, estimatedSettlementDate: valuationDate, discountRate: discountRateString } = incidentDetails; // Added discountRateString

  if (!incidentDate || !valuationDate) {
    // console.error("Missing incidentDate or estimatedSettlementDate (valuationDate).");
    return { summary: { totalPastLoss: 0, totalFutureLoss: 0, totalFuturePresentValue: 0, preJudgementInterest: 0, presentValueDiscount: 0, grandTotalLoss: 0 }, detailedPastLosses: [], detailedFutureLosses: [] };
  }

  const incidentD = safeParseISO(incidentDate);
  const valuationD = safeParseISO(valuationDate);
  const lifeExpectancyD = lifeExpectancyDateString ? safeParseISO(lifeExpectancyDateString) : null;
  const retirementD = retirementDateString ? safeParseISO(retirementDateString) : null;
  let calculatedDiscountR;
  const rateStr = String(discountRateString ?? '0');

  if (rateStr.includes('%')) {
    const parsedValue = parseFloat(rateStr.replace('%', ''));
    calculatedDiscountR = (!isNaN(parsedValue) && parsedValue >= 0) ? parsedValue / 100 : 0.0;
  } else {
    const parsedValue = parseFloat(rateStr);
    if (!isNaN(parsedValue) && parsedValue >= 0) {
      if (parsedValue >= 0.1 || parsedValue === 0) {
        calculatedDiscountR = parsedValue / 100;
      } else {
        calculatedDiscountR = parsedValue;
      }
    } else {
      calculatedDiscountR = 0.0;
    }
  }
  const discountR = calculatedDiscountR;

  // Growth Rate Parsing
  let calculatedGrowthR = 0.0;
  const growthRateStrInput = String(growthRateStringRaw ?? '0');

  if (growthRateStrInput.includes('%')) {
    const parsedValue = parseFloat(growthRateStrInput.replace('%', ''));
    calculatedGrowthR = !isNaN(parsedValue) ? parsedValue / 100 : 0.0;
  } else {
    const parsedValue = parseFloat(growthRateStrInput);
    if (!isNaN(parsedValue)) {
      // If abs(value) >= 1 (e.g., 2, -2, 50) or value is 0, assume it's a percentage that needs division by 100.
      // Otherwise (e.g., 0.02, -0.01, 0.5), assume it's already a decimal rate.
      if (Math.abs(parsedValue) >= 1 || parsedValue === 0) {
        calculatedGrowthR = parsedValue / 100;
      } else {
        calculatedGrowthR = parsedValue;
      }
    } else {
      calculatedGrowthR = 0.0; // Default if parsing fails
    }
  }
  const growthR = calculatedGrowthR;

  if (!incidentD || !valuationD) {
    // console.error("calculateLossOfIncome: Invalid incidentDate or valuationDate.");
    // Consider returning the same structure as calculateLostIncomeDetailsForStep4 for consistency if erroring
    return { summary: { totalPastLoss: 0, totalFutureLoss: 0, totalFuturePresentValue: 0, preJudgementInterest: 0, presentValueDiscount: 0, grandTotalLoss: 0 }, detailedPastLosses: [], detailedFutureLosses: [] };
  }

  let globalCapDate = null;
  if (retirementD && lifeExpectancyD) {
    globalCapDate = retirementD < lifeExpectancyD ? retirementD : lifeExpectancyD;
  } else if (retirementD) {
    globalCapDate = retirementD;
  } else if (lifeExpectancyD) {
    globalCapDate = lifeExpectancyD;
  }

  let totalPastLossNet = 0;
  let totalFutureLossNet = 0;
  let totalFuturePresentValue = 0; // New accumulator for PV sum
  let totalFuturePresentValueDiscountAmount = 0; // New accumulator for PV discount sum

  const pastLossesByYearStore = {};
  const futureLossesByYearStore = {};

  for (const rawSource of lossOfIncome.sourcesOfIncome) {
    const source = { ...rawSource };
    if (typeof source.amount === 'string') {
        source.amount = parseFloat(source.amount);
    }

    if (!source.startDate || typeof source.amount !== 'number' || isNaN(source.amount) || !source.payInterval) {
        // console.warn("Skipping income source due to missing critical data (startDate, amount, or payInterval):", source.description);
        continue;
    }
    const sourceStartD = safeParseISO(source.startDate);
    let intrinsicSourceEndD = determineSourceEndDate(source);

    // Determine effectiveSourceEndD by applying globalCapDate
    let effectiveSourceEndD = intrinsicSourceEndD;
    if (globalCapDate) {
      if (effectiveSourceEndD) { // If source has an intrinsic end date
        effectiveSourceEndD = effectiveSourceEndD < globalCapDate ? effectiveSourceEndD : globalCapDate;
      } else { // If source has no intrinsic end date (e.g. determineSourceEndDate returned undefined)
        // This case might mean the source is intended to be indefinite until a global cap.
        // If determineSourceEndDate can return undefined for 'Lifetime' type sources not yet fully handled,
        // then capping it here is appropriate.
        effectiveSourceEndD = globalCapDate;
      }
    }

    if (!sourceStartD || !effectiveSourceEndD) {
      // console.warn("calculateLossOfIncome: Could not determine start/effective end date for source, skipping:", source.description);
      continue;
    }

    if (effectiveSourceEndD < sourceStartD) {
        // console.warn("calculateLossOfIncome: Income source effective end date is before start date, skipping:", source.description);
        continue;
    }
    
    const lostEarningCapacity = typeof source.lostEarningCapacity === 'number' ? source.lostEarningCapacity : 1.0;

    // Calculate growth parameters once per source
    const payPeriodsPerYear = getPayPeriodsPerYear(source.payInterval);
    // perPeriodGrowthRate will be 0 if growthR is 0 or payPeriodsPerYear is invalid/zero
    const perPeriodGrowthRate = calculatePerPeriodGrowthRate(growthR, payPeriodsPerYear);

    // --- Past Loss Calculation ---
    const pastLossEffectiveStart = sourceStartD > incidentD ? sourceStartD : incidentD;
    // Cap the past loss period by the effectiveSourceEndD AND valuationD
    const pastLossPotentialEnd = valuationD < effectiveSourceEndD ? valuationD : effectiveSourceEndD;
    
    if (pastLossPotentialEnd >= pastLossEffectiveStart) {
      const pastMonthlySegments = getMonthlyPeriods(pastLossEffectiveStart, pastLossPotentialEnd);
      for (const segment of pastMonthlySegments) {
        let incomeAmountForPastSegment = source.amount; // Start with base amount

        if (perPeriodGrowthRate !== 0) { // Apply growth if there's a valid per-period rate
            const numGrowthApplications = calculateNumberOfGrowthApplications(
                sourceStartD,        // Growth always starts from the source's own start date
                segment.start,       // Up to the beginning of the current past segment
                payPeriodsPerYear
            );

            if (numGrowthApplications > 0) {
                incomeAmountForPastSegment = source.amount * Math.pow(1 + perPeriodGrowthRate, numGrowthApplications);
            }
        }

        const segmentGrossIncome = calculateIncomeForSegment(
          segment.start,
          segment.end,
          incomeAmountForPastSegment, // Use growth-adjusted income
          source.payInterval
        );
        const segmentNetLoss = segmentGrossIncome * lostEarningCapacity;
        totalPastLossNet += segmentNetLoss;
        const year = segment.start.getFullYear();

        if (!pastLossesByYearStore[year]) {
          pastLossesByYearStore[year] = {
            year: year,
            periods: [], // This will now store source summaries for the year
            annualGrossIncome: 0, // Overall annual total
            annualNetLoss: 0,     // Overall annual total
          };
        }

        // Find or create a summary for this specific source within this year
        let sourceInYearSummaryPast = pastLossesByYearStore[year].periods.find(p => p.originalSourceId === (source.id || source.trackingKey));
        if (!sourceInYearSummaryPast) {
          sourceInYearSummaryPast = {
            originalSourceId: source.id || source.trackingKey,
            description: source.description || "N/A",
            lostCapacity: lostEarningCapacity,
            payInterval: source.payInterval,
            originalAmount: source.amount,
            totalGrossIncomeInYear: 0,
            totalNetLossInYear: 0,
            firstStartDateInYear: formatDate(segment.start),
            lastEndDateInYear: formatDate(segment.end),
          };
          pastLossesByYearStore[year].periods.push(sourceInYearSummaryPast);
        }

        sourceInYearSummaryPast.totalGrossIncomeInYear += segmentGrossIncome;
        sourceInYearSummaryPast.totalNetLossInYear += segmentNetLoss;
        if (safeParseISO(sourceInYearSummaryPast.lastEndDateInYear) < segment.end) {
            sourceInYearSummaryPast.lastEndDateInYear = formatDate(segment.end);
        }
        if (safeParseISO(sourceInYearSummaryPast.firstStartDateInYear) > segment.start) {
            sourceInYearSummaryPast.firstStartDateInYear = formatDate(segment.start);
        }

        // Accumulate overall annual totals
        pastLossesByYearStore[year].annualGrossIncome += segmentGrossIncome;
        pastLossesByYearStore[year].annualNetLoss += segmentNetLoss;
      }
    }

    // --- Future Loss Calculation ---
    const futureLossPeriodStartFromValuation = addDays(valuationD, 1); // valuationD is already start of day
    // Determine the actual start for future loss calculation period.
    // It should not begin before the day after the valuation date.
    // It also should not begin before the source's own start date.
    let actualFutureCalculationStart = futureLossPeriodStartFromValuation;
    if (sourceStartD > futureLossPeriodStartFromValuation) {
        actualFutureCalculationStart = sourceStartD;
    }
    // Future loss period ends at the effectiveSourceEndD
    const futureLossPotentialEnd = effectiveSourceEndD;
    
    // Ensure there's a valid period for future loss calculation
    if (futureLossPotentialEnd >= actualFutureCalculationStart) {
      // payPeriodsPerYear and perPeriodGrowthRate are now calculated at the source level
    
      const futureMonthlySegments = getMonthlyPeriods(actualFutureCalculationStart, futureLossPotentialEnd);
      for (const segment of futureMonthlySegments) {
        // const currentSegmentYear = segment.start.getFullYear(); // OLD
        // const yearsElapsed = Math.max(0, currentSegmentYear - yearOfFutureLossStart); // OLD
        // const incomeAmountForThisSegmentOld = source.amount * Math.pow((1 + growthR), yearsElapsed); // OLD

        let incomeAmountForThisSegment = source.amount; // Start with base amount

        if (perPeriodGrowthRate !== 0) { // Apply growth if there's a valid per-period rate
            // Growth is compounded from the source's actual start date (sourceStartD).
            // Apply growth based on how many pay periods have effectively passed
            // from sourceStartD up to the beginning of the current segment.
            const numGrowthApplications = calculateNumberOfGrowthApplications(
                sourceStartD,             // Growth counting always begins from the income source's start date
                segment.start,            // Reference date: up to the beginning of the current segment
                payPeriodsPerYear         // Pay periods for this specific income source
            );

            if (numGrowthApplications > 0) {
                incomeAmountForThisSegment = source.amount * Math.pow(1 + perPeriodGrowthRate, numGrowthApplications);
            }
        }

        const segmentGrossIncome = calculateIncomeForSegment(
          segment.start,
          segment.end,
          incomeAmountForThisSegment, // Use NEW growth-adjusted income
          source.payInterval
        );
        const segmentNetLoss = segmentGrossIncome * lostEarningCapacity;
        totalFutureLossNet += segmentNetLoss;
        const year = segment.start.getFullYear();

        if (!futureLossesByYearStore[year]) {
          futureLossesByYearStore[year] = {
            year: year,
            periods: [], // This will now store source summaries for the year
            annualGrossIncome: 0, // Overall annual total
            annualNetLoss: 0,     // Overall annual total
            annualPresentValue: 0,
            annualPresentValueDiscountAmount: 0,
          };
        }

        let segmentPresentValue = segmentNetLoss;
        let segmentPresentValueDiscountVal = 0;

        if (valuationD && discountR > 0) {
          const FV = segmentNetLoss;
          const r = discountR;
          const daysDifference = (segment.end.getTime() - valuationD.getTime()) / (1000 * 60 * 60 * 24);
          const n = daysDifference / 365.25;

          if (n > 0) {
            const PV = FV / Math.pow((1 + r), n);
            segmentPresentValue = parseFloat(PV.toFixed(2));
            segmentPresentValueDiscountVal = parseFloat((FV - PV).toFixed(2));
          }
        }
        // These top-level accumulators are for the grand summary object
        totalFuturePresentValue += segmentPresentValue;
        totalFuturePresentValueDiscountAmount += segmentPresentValueDiscountVal;
        
        // Find or create a summary for this specific source within this year
        let sourceInYearSummaryFuture = futureLossesByYearStore[year].periods.find(p => p.originalSourceId === (source.id || source.trackingKey));
        if (!sourceInYearSummaryFuture) {
          sourceInYearSummaryFuture = {
            originalSourceId: source.id || source.trackingKey,
            description: source.description || "N/A",
            lostCapacity: lostEarningCapacity,
            payInterval: source.payInterval,
            originalAmount: source.amount,
            totalGrossIncomeInYear: 0,
            totalNetLossInYear: 0,
            presentValue: 0,
            presentValueDiscountAmount: 0,
            firstStartDateInYear: formatDate(segment.start),
            lastEndDateInYear: formatDate(segment.end),
          };
          futureLossesByYearStore[year].periods.push(sourceInYearSummaryFuture);
        }

        sourceInYearSummaryFuture.totalGrossIncomeInYear += segmentGrossIncome;
        sourceInYearSummaryFuture.totalNetLossInYear += segmentNetLoss;
        sourceInYearSummaryFuture.presentValue += segmentPresentValue;
        sourceInYearSummaryFuture.presentValueDiscountAmount += segmentPresentValueDiscountVal;
        if (safeParseISO(sourceInYearSummaryFuture.lastEndDateInYear) < segment.end) {
            sourceInYearSummaryFuture.lastEndDateInYear = formatDate(segment.end);
        }
        if (safeParseISO(sourceInYearSummaryFuture.firstStartDateInYear) > segment.start) {
            sourceInYearSummaryFuture.firstStartDateInYear = formatDate(segment.start);
        }

        // Accumulate overall annual totals
        futureLossesByYearStore[year].annualGrossIncome += segmentGrossIncome;
        futureLossesByYearStore[year].annualNetLoss += segmentNetLoss;
        futureLossesByYearStore[year].annualPresentValue += segmentPresentValue;
        futureLossesByYearStore[year].annualPresentValueDiscountAmount += segmentPresentValueDiscountVal;
      }
    }
  }

  const pastLossesByYear = Object.values(pastLossesByYearStore).sort((a, b) => a.year - b.year);
  const futureLossesByYear = Object.values(futureLossesByYearStore).sort((a, b) => a.year - b.year);

  return {
    summary: {
      totalPastLoss: totalPastLossNet,
      totalFutureLoss: totalFutureLossNet, // Net future loss before PV
      totalFuturePresentValue: totalFuturePresentValue, // Sum of PV of future losses
      preJudgementInterest: 0, // Placeholder
      presentValueDiscount: totalFuturePresentValueDiscountAmount, // Sum of (FV - PV)
      grandTotalLoss: totalPastLossNet + totalFuturePresentValue, // Past Loss + PV of Future Loss
    },
    detailedPastLosses: pastLossesByYear,
    detailedFutureLosses: futureLossesByYear,
  };
}

// --- Utility Functions for Formatting and Parsing ---

export function formatCurrency(value) {
  if (typeof value !== 'number') return value; // Or handle as error, e.g., return '$0.00' or throw
  return value.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}


export function formatDate(dateStringOrObject) {
  if (!dateStringOrObject) return null;

  let dateObj;

  // Handle Firestore Timestamp objects
  if (
    typeof dateStringOrObject === 'object' &&
    dateStringOrObject !== null &&
    typeof dateStringOrObject.seconds === 'number' &&
    typeof dateStringOrObject.nanoseconds === 'number'
  ) {
    // Convert Firestore Timestamp to JS Date
    dateObj = new Date(dateStringOrObject.seconds * 1000 + Math.floor(dateStringOrObject.nanoseconds / 1e6));
  } else if (typeof dateStringOrObject === 'string') {
    // Always use parseISO for string dates to avoid timezone issues.
    // This correctly handles both "YYYY-MM-DD" and full ISO strings.
    dateObj = safeParseISO(dateStringOrObject);
  } else if (dateStringOrObject instanceof Date) {
    dateObj = dateStringOrObject;
  } else {
    return String(dateStringOrObject); // Or handle as error
  }

  if (isNaN(dateObj.getTime())) {
    return String(dateStringOrObject); // Return original if date is invalid
  }

  // Format to "Jun 6, 2026" style
  return formatDateFns(dateObj, 'MMM d, yyyy');
}


export function safeParseISO(dateString) {
  if (!dateString) return null;

  // Check if it's already a Date object (though typically we expect strings from inputs)
  if (dateString instanceof Date) {
    if (!isNaN(dateString.getTime())) {
      return getStartOfDay(dateString); // Normalize to start of day
    } else {
      // console.warn("safeParseISO received an invalid Date object:", dateString);
      return null;
    }
  }

  if (typeof dateString !== 'string') {
    // console.warn("safeParseISO expects a string or Date object, received:", typeof dateString, dateString);
    return null;
  }
  
  // Regex for YYYY-MM-DD format. Does not validate day/month ranges strictly but good enough for parsing.
  const simpleDateRegex = /^\d{4}-\d{2}-\d{2}$/;

  let date;
  if (simpleDateRegex.test(dateString)) {
    // For "YYYY-MM-DD", parse as UTC to avoid timezone issues, then get start of that UTC day.
    const [year, month, day] = dateString.split('-').map(Number);
    // Treat YYYY-MM-DD as local date to avoid timezone shift to previous day
    date = new Date(year, month - 1, day);
  } else {
    // For full ISO strings like "YYYY-MM-DDTHH:mm:ss.sssZ", new Date() handles them correctly.
    // This will also handle cases where the string might already be in UTC.
    date = new Date(dateString);
  }

  if (isNaN(date.getTime())) {
    // console.warn(`safeParseISO: Failed to parse date string: "${dateString}"`);
    return null;
  }
  return getStartOfDay(date); // Normalize to start of day in local timezone context after parsing
}


// This function is specifically for Step4's detailed breakdown.
// It mirrors some logic from calculateLossOfIncome but is tailored for display purposes
// and might not include all capping/PV logic if not needed for that specific step's view.
export function calculateLostIncomeDetailsForStep4({
  sourcesOfIncome,
  incidentDate,
  estimatedSettlementDate, // This is the valuation date
  lifeExpectancyDate,
  retirementDate,
}) {
  const incidentD = safeParseISO(incidentDate);
  const valuationD = safeParseISO(estimatedSettlementDate);
  const lifeExpectancyD = lifeExpectancyDate ? safeParseISO(lifeExpectancyDate) : null;
  const retirementD = retirementDate ? safeParseISO(retirementDate) : null;

  if (!incidentD || !valuationD) {
    // console.error("Step4 Details: Invalid incidentDate or valuationDate.");
    return { pastLossesByYear: [], futureLossesByYear: [], summary: { totalPastLoss: 0, totalFutureLoss: 0 } };
  }

  let globalCapDate = null;
  if (retirementD && lifeExpectancyD) {
    globalCapDate = retirementD < lifeExpectancyD ? retirementD : lifeExpectancyD;
  } else if (retirementD) {
    globalCapDate = retirementD;
  } else if (lifeExpectancyD) {
    globalCapDate = lifeExpectancyD;
  }

  let grandTotalPastLossNet = 0;
  let grandTotalFutureLossNet = 0;

  const pastLossesByYearStore = {};
  const futureLossesByYearStore = {};

  (sourcesOfIncome || []).forEach(source => {
    if (!source.startDate || typeof source.amount !== 'number' || !source.payInterval) {
      // console.warn("Step4 Details: Skipping income source due to missing data:", source.description);
      return;
    }

    const sourceStartD = safeParseISO(source.startDate);
    let intrinsicSourceEndD = determineSourceEndDate(source);

    let effectiveSourceEndD = intrinsicSourceEndD;
    if (globalCapDate) {
      if (effectiveSourceEndD) {
        effectiveSourceEndD = effectiveSourceEndD < globalCapDate ? effectiveSourceEndD : globalCapDate;
      } else {
        effectiveSourceEndD = globalCapDate;
      }
    }
    
    if (!sourceStartD || !effectiveSourceEndD) {
      // console.warn("Step4 Details: Could not determine start/effective end for source:", source.description);
      return;
    }
    if (effectiveSourceEndD < sourceStartD) {
        // console.warn("Step4 Details: Income source effective end date is before start date, skipping:", source.description);
        return;
    }

    const lostEarningCapacity = typeof source.lostEarningCapacity === 'number' ? source.lostEarningCapacity : 1.0;

    // Past Loss for Step 4
    const pastLossEffectiveStart = sourceStartD > incidentD ? sourceStartD : incidentD;
    const pastLossPotentialEnd = valuationD < effectiveSourceEndD ? valuationD : effectiveSourceEndD;

    if (pastLossPotentialEnd >= pastLossEffectiveStart) {
      const pastMonthlySegments = getMonthlyPeriods(pastLossEffectiveStart, pastLossPotentialEnd);
      for (const segment of pastMonthlySegments) {
        const segmentGrossIncome = calculateIncomeForSegment(segment.start, segment.end, source.amount, source.payInterval);
        const segmentNetLoss = segmentGrossIncome * lostEarningCapacity;
        grandTotalPastLossNet += segmentNetLoss;
        const year = segment.start.getFullYear();

        if (!pastLossesByYearStore[year]) {
          pastLossesByYearStore[year] = {
            year: year,
            periods: [],
            annualGrossIncome: 0,
            annualNetLoss: 0,
          };
        }
        let sourceInYearSummary = pastLossesByYearStore[year].periods.find(p => p.originalSourceId === (source.id || source.trackingKey));
        if (!sourceInYearSummary) {
          sourceInYearSummary = {
            originalSourceId: source.id || source.trackingKey,
            description: source.description || "N/A",
            lostCapacity: lostEarningCapacity,
            payInterval: source.payInterval,
            originalAmount: source.amount,
            totalGrossIncomeInYear: 0,
            totalNetLossInYear: 0,
            firstStartDateInYear: formatDate(segment.start),
            lastEndDateInYear: formatDate(segment.end),
          };
          pastLossesByYearStore[year].periods.push(sourceInYearSummary);
        }
        sourceInYearSummary.totalGrossIncomeInYear += segmentGrossIncome;
        sourceInYearSummary.totalNetLossInYear += segmentNetLoss;
         if (safeParseISO(sourceInYearSummary.lastEndDateInYear) < segment.end) {
            sourceInYearSummary.lastEndDateInYear = formatDate(segment.end);
        }
        if (safeParseISO(sourceInYearSummary.firstStartDateInYear) > segment.start) {
            sourceInYearSummary.firstStartDateInYear = formatDate(segment.start);
        }
        pastLossesByYearStore[year].annualGrossIncome += segmentGrossIncome;
        pastLossesByYearStore[year].annualNetLoss += segmentNetLoss;
      }
    }

    // Future Loss for Step 4 (No PV needed for this view usually)
    const futureLossPeriodStartFromValuation = addDays(valuationD, 1);
    const futureLossEffectiveStart = sourceStartD > futureLossPeriodStartFromValuation ? sourceStartD : futureLossPeriodStartFromValuation;
    const futureLossPotentialEnd = effectiveSourceEndD;

    if (futureLossPotentialEnd >= futureLossEffectiveStart) {
      const futureMonthlySegments = getMonthlyPeriods(futureLossEffectiveStart, futureLossPotentialEnd);
      for (const segment of futureMonthlySegments) {
        const segmentGrossIncome = calculateIncomeForSegment(segment.start, segment.end, source.amount, source.payInterval);
        const segmentNetLoss = segmentGrossIncome * lostEarningCapacity;
        grandTotalFutureLossNet += segmentNetLoss;
        const year = segment.start.getFullYear();

        if (!futureLossesByYearStore[year]) {
          futureLossesByYearStore[year] = {
            year: year,
            periods: [],
            annualGrossIncome: 0,
            annualNetLoss: 0,
            // No PV fields needed for Step 4 summary usually
          };
        }
        // Find or create a summary for this specific source within this year
        let sourceInYearSummaryFuture = futureLossesByYearStore[year].periods.find(p => p.originalSourceId === (source.id || source.trackingKey));
        if (!sourceInYearSummaryFuture) {
          sourceInYearSummaryFuture = {
            originalSourceId: source.id || source.trackingKey,
            description: source.description || "N/A",
            lostCapacity: lostEarningCapacity,
            payInterval: source.payInterval,
            originalAmount: source.amount,
            totalGrossIncomeInYear: 0,
            totalNetLossInYear: 0,
            // No PV fields
            firstStartDateInYear: formatDate(segment.start),
            lastEndDateInYear: formatDate(segment.end),
          };
          futureLossesByYearStore[year].periods.push(sourceInYearSummaryFuture);
        }

        sourceInYearSummaryFuture.totalGrossIncomeInYear += segmentGrossIncome;
        sourceInYearSummaryFuture.totalNetLossInYear += segmentNetLoss;
        if (safeParseISO(sourceInYearSummaryFuture.lastEndDateInYear) < segment.end) {
            sourceInYearSummaryFuture.lastEndDateInYear = formatDate(segment.end);
        }
        if (safeParseISO(sourceInYearSummaryFuture.firstStartDateInYear) > segment.start) {
            sourceInYearSummaryFuture.firstStartDateInYear = formatDate(segment.start);
        }

        futureLossesByYearStore[year].annualGrossIncome += segmentGrossIncome;
        futureLossesByYearStore[year].annualNetLoss += segmentNetLoss;
      }
    }
  });

  const pastLossesByYear = Object.values(pastLossesByYearStore).sort((a, b) => a.year - b.year);
  const futureLossesByYear = Object.values(futureLossesByYearStore).sort((a, b) => a.year - b.year);
  
  return {
    pastLossesByYear,
    futureLossesByYear,
    summary: {
      totalPastLoss: grandTotalPastLossNet,
      totalFutureLoss: grandTotalFutureLossNet,
    }
  };
}


export function generate1YearPeriods(startDate, endDate) {
  const start = safeParseISO(startDate);
  const end = safeParseISO(endDate);
  const periods = [];
  let currentYearStart = start;

  while (currentYearStart <= end) {
    const yearEnd = getStartOfDay(new Date(currentYearStart.getFullYear() + 1, currentYearStart.getMonth(), currentYearStart.getDate() -1));
    const periodEnd = yearEnd < end ? yearEnd : end;
    periods.push({ start: new Date(currentYearStart), end: new Date(periodEnd) });
    currentYearStart = addDays(periodEnd, 1);
  }
  return periods;
}

export function calculatePeriods(periodDefs, baseDate, lifeExpectancyYears, worklifeExpectancyYears, discountRate, growthRate) {
  // This is a placeholder for a more complex calculation logic
  // based on period definitions, life/worklife expectancy, rates, etc.
  // For now, it returns a simplified structure.
  console.warn("TODO: implement Calculate Periods for life care plan PV");
  return {
    calculatedPeriods: [],
    summary: "Calculations based on inputs will be shown here."
  };
}